import {
  CAMPOS_DE_FINANCIACION,
  ETIQUETA_INSTRUMENTO,
  ETIQUETA_INSTRUMENTO_EN,
  INSTRUMENTOS_DE_CREDITO,
  ETIQUETA_CAMPO_FINANCIACION,
  ETIQUETA_CAMPO_FINANCIACION_EN,
  ETIQUETA_ESTADO_FINANCIACION,
  ETIQUETA_ESTADO_FINANCIACION_EN,
  ETIQUETA_GRUPO_FINANCIACION,
  ETIQUETA_GRUPO_FINANCIACION_EN,
  ETIQUETA_PASO_CADENA,
  ETIQUETA_PASO_CADENA_EN,
  GRUPOS_DE_FINANCIACION,
  type CampoDeFinanciacion,
} from '@cnc/contracts';
import {useEffect, useRef, useState} from 'react';
import {Link, useLocation} from 'react-router';
import {
  useBancosDeFinanciador,
  useFinanciacionDeExportacion,
  useFinanciadores,
  useGuardarFinanciacion,
} from '../api/financiacion';
import {et, tr, type Texto} from '../i18n/idioma';
import {fecha, fechaHora, numero, textoOGuion} from '../utilidades/formato';
import {AYUDA_FINANCIACION as F} from './ayuda-exportacion';
import {NotaDeAyuda} from './nota-de-ayuda';
import {usePermisos} from './sesion-contexto';

const usd = (v: number | null) => (v === null ? '—' : `USD ${numero(v, 2)}`);
const tasa = (v: number | null) => (v === null ? '—' : `${numero(v * 100, 2)} %`);
const siNo = (v: boolean | null) => (v === null ? '—' : v ? tr('Sí', 'Yes') : tr('No', 'No'));

// Financiacion de una exportacion (decision 0021). Se trabaja en la app:
// editores y administradores la editan o la registran; los datos iniciales
// vienen del Status BLQ y del Intercompany Export Chain.
export function FinanciacionDeExportacion({codigo}: {codigo: string}) {
  const consulta = useFinanciacionDeExportacion(codigo);
  const permisos = usePermisos();
  const [editando, setEditando] = useState(false);
  const x = consulta.data?.financiacion;
  const {hash} = useLocation();
  const seccion = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (hash === '#financiacion' && consulta.data) seccion.current?.scrollIntoView({block: 'start'});
  }, [hash, consulta.data]);
  const estados = et(ETIQUETA_ESTADO_FINANCIACION, ETIQUETA_ESTADO_FINANCIACION_EN);
  const pasos = et(ETIQUETA_PASO_CADENA, ETIQUETA_PASO_CADENA_EN);

  const grupos: Array<[string, Array<[string, string, Texto]>]> = x
    ? [
        [
          tr('Financiación', 'Financing'),
          [
            [tr('Financiador', 'Financer'), textoOGuion(x.financiadorNombre ?? x.financiador), F.financiador],
            [tr('Estado', 'Status'), estados[x.estado], F.estado],
            [tr('Factura al financiador', 'Invoice to the financer'), textoOGuion(x.factura), F.factura],
            [tr('Referencia', 'Reference'), textoOGuion(x.referencia), F.referencia],
            [tr('Financiado', 'Financed'), usd(x.financiadoUsd), F.financiadoUsd],
            [
              tr('Desembolso esperado', 'Expected disbursement'),
              fecha(x.desembolsoEsperado) +
                (x.desembolsoSegun === 'seguimiento'
                  ? tr(' ↻ (del seguimiento: día del zarpe)', ' ↻ (from the tracking: departure day)')
                  : ''),
              F.desembolsoEsperado,
            ],
            [tr('Desembolso', 'Disbursement'), fecha(x.desembolso), F.desembolso],
            [
              tr('Pago inicial recibido', 'Initial payment received'),
              siNo(x.pagoInicialRecibido),
              F.pagoInicialRecibido,
            ],
            [
              tr('Saldo pedido', 'Balance requested'),
              x.saldoPedidoEl ? fecha(x.saldoPedidoEl) : siNo(x.saldoPedido),
              F.saldoPedido,
            ],
            [
              tr('Saldo pagado', 'Balance paid'),
              x.saldoPagadoUsd !== null ? `${usd(x.saldoPagadoUsd)} · ${fecha(x.saldoPagadoEl)}` : siNo(x.saldoPagado),
              F.saldoPagado,
            ],
          ],
        ],
        [
          tr('Comprador', 'Buyer'),
          [
            [tr('Paga al financiador', 'Pays the financer'), textoOGuion(x.pagaAlFinanciador), F.pagaAlFinanciador],
            [tr('Paga', 'Pays'), usd(x.valorCompradorUsd), F.valorCompradorUsd],
            [
              tr('Fecha de pago', 'Payment date'),
              fecha(x.pagoComprador) +
                (x.pagoCompradorSegun === 'seguimiento'
                  ? tr(
                      ' ↻ (del seguimiento: días hábiles antes de la ETA a Mundra; 5 en BLQ, 3 en Mineros)',
                      ' ↻ (from the tracking: business days before the ETA in Mundra; 5 for BLQ, 3 for Mineros)',
                    )
                  : ''),
              F.pagoComprador,
            ],
            [tr('Pagó el', 'Paid on'), fecha(x.pagadoEl), F.pagadoEl],
            [tr('Factura al comprador', 'Invoice to the buyer'), textoOGuion(x.facturaComprador), F.facturaComprador],
            [tr('Valor total estimado', 'Estimated total value'), usd(x.valorTotalUsd), F.valorTotalUsd],
            [
              tr('Venta proyectada', 'Projected sale'),
              `${usd(x.valorVentaUsd)} · ${fecha(x.fechaVenta)}`,
              F.valorVentaUsd,
            ],
            [tr('Cambio de BL pedido', 'BL change requested'), fecha(x.cambioBlPedidoEl), F.cambioBlPedidoEl],
            [
              tr('BL recogido por el financiador', 'BL picked up by the financer'),
              fecha(x.blRecogidoEl),
              F.blRecogidoEl,
            ],
          ],
        ],
        [
          tr('Cadena intercompany', 'Intercompany chain'),
          [
            [tr('Cadena', 'Chain'), textoOGuion(x.cadena), F.cadena],
            [tr('Vendedor', 'Seller'), textoOGuion(x.vendedor), F.vendedor],
            [tr('Comprador', 'Buyer'), textoOGuion(x.comprador), F.comprador],
            [tr('Agente de carga', 'Freight agent'), textoOGuion(x.agenteDeCarga), F.agenteDeCarga],
            [tr('Planta', 'Facility'), textoOGuion(x.planta), F.planta],
            [tr('Fijación de precios', 'Price fixing'), fecha(x.fijacionEl), F.fijacionEl],
            [tr('Precio Au / Ag', 'Au / Ag price'), `${usd(x.precioAuUsd)} / ${usd(x.precioAgUsd)}`, F.precioAuUsd],
            [tr('Factura CM / MM', 'CM / MM invoice'), usd(x.facturaGrupoUsd), F.facturaGrupoUsd],
            [tr('Factura final', 'Final invoice'), usd(x.facturaFinalUsd), F.facturaFinalUsd],
            [tr('Estado de la cadena', 'Chain status'), textoOGuion(x.estadoCadena), F.estadoCadena],
          ],
        ],
        [
          tr('Costos de la financiación', 'Financing costs'),
          [
            ['SOFR', tasa(x.sofr), F.sofr],
            [tr('Costo de fondos', 'Cost of funds'), tasa(x.costoDeFondos), F.costoDeFondos],
            [tr('Margen del financiador', 'Financer margin'), tasa(x.margenFinanciador), F.margenFinanciador],
            [tr('Comisión inicial', 'Initial fee'), tasa(x.comisionInicial), F.comisionInicial],
            [tr('Cargo fijo', 'Fixed fee'), usd(x.cargoFijoUsd), F.cargoFijoUsd],
            [
              tr('Tratamiento provisional', 'Provisional treatment'),
              usd(x.tratamientoProvisionalUsd),
              F.tratamientoProvisionalUsd,
            ],
            [tr('Tratamiento final', 'Final treatment'), usd(x.tratamientoFinalUsd), F.tratamientoFinalUsd],
            [tr('Muestreo final', 'Final sampling'), usd(x.muestreoFinalUsd), F.muestreoFinalUsd],
            [tr('Otros', 'Other'), usd(x.otrosCostosUsd), F.otrosCostosUsd],
            [tr('Banco de la refinería', 'Refinery bank'), textoOGuion(x.bancoRefineria), F.bancoRefineria],
          ],
        ],
      ]
    : [];

  return (
    <div
      ref={seccion}
      id="financiacion"
      className="scroll-mt-4 rounded-lg border border-linea bg-white p-5"
      data-prueba="financiacion"
    >
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h2 className="font-medium">{tr('Financiación', 'Financing')}</h2>
          <p className="mt-1 text-xs text-apagado">
            {x?.editadoEl
              ? tr(
                  `Editada en la app por ${x.editadoPor ?? 'alguien'} el ${fechaHora(x.editadoEl)}.`,
                  `Edited in the app by ${x.editadoPor ?? 'someone'} on ${fechaHora(x.editadoEl)}.`,
                )
              : tr(
                  'Se trabaja aquí; los datos iniciales vienen del Status BLQ y del Intercompany Export Chain.',
                  'Worked on here; the initial data comes from the Status BLQ and the Intercompany Export Chain.',
                )}{' '}
            <Link
              to={`/seguimiento/financiaciones?buscar=${encodeURIComponent(codigo)}`}
              className="text-verde hover:underline"
            >
              {tr('Ver en la pestaña Financiaciones', 'See in the Financing tab')}
            </Link>
          </p>
        </div>
        {permisos.editar && consulta.data && !editando && (
          <button
            type="button"
            onClick={() => setEditando(true)}
            className="rounded-md border border-linea bg-white px-3 py-1.5 text-sm hover:bg-papel"
          >
            {x
              ? tr('✎ Editar financiación', '✎ Edit financing')
              : tr('+ Registrar financiación', '+ Register financing')}
          </button>
        )}
      </div>
      {editando && consulta.data && (
        <div className="mt-3">
          <FormularioDeFinanciacion
            codigo={codigo}
            campos={x?.campos ?? {}}
            version={x?.version ?? null}
            onCerrar={() => setEditando(false)}
          />
        </div>
      )}
      <div className={`mt-3 space-y-4 ${editando ? 'hidden' : ''}`}>
        {consulta.isPending && <p className="text-sm text-apagado">{tr('Consultando…', 'Loading…')}</p>}
        {consulta.isError && <p className="text-sm text-alerta">{consulta.error.message}</p>}
        {consulta.data && !x && (
          <p className="text-sm text-apagado">
            {tr('Esta exportación no tiene datos de financiación.', 'This export has no financing data.')}
          </p>
        )}
        {x?.paraRevisar && x.nota && (
          <p className="rounded-md border border-dorado/50 bg-dorado-claro px-3 py-2 text-sm">
            ⚠ {tr('Para revisar:', 'To review:')} {x.nota}
          </p>
        )}
        {x?.financiadoIntercompanyUsd !== null && x?.financiadoIntercompanyUsd !== undefined && (
          <p className="text-sm">
            {tr(
              `El Intercompany dice otro monto financiado: ${usd(x.financiadoIntercompanyUsd)}. Se muestra el del Status BLQ, que es más reciente.`,
              `The Intercompany shows another financed amount: ${usd(x.financiadoIntercompanyUsd)}. The Status BLQ one is shown, as it is more recent.`,
            )}
          </p>
        )}
        {x && (
          <div className="grid gap-4 md:grid-cols-2">
            {grupos.map(([titulo, filas]) => (
              <div key={titulo}>
                <h3 className="text-xs font-semibold uppercase tracking-wide text-apagado">{titulo}</h3>
                <dl className="mt-1 space-y-0.5 text-sm">
                  {filas.map(([k, v, ayuda]) => (
                    <div key={k} className="flex justify-between gap-3">
                      <dt className="text-apagado">
                        {k}
                        <NotaDeAyuda titulo={k} texto={ayuda} />
                      </dt>
                      <dd className="text-right tabular-nums">{v}</dd>
                    </div>
                  ))}
                </dl>
              </div>
            ))}
          </div>
        )}
        {x && x.pasos.length > 0 && (
          <div>
            <h3 className="text-xs font-semibold uppercase tracking-wide text-apagado">
              {tr('Pasos de la cadena', 'Chain steps')}
              <NotaDeAyuda titulo={tr('Pasos de la cadena', 'Chain steps')} texto={F.pasos} />
            </h3>
            <ul className="mt-1 flex flex-wrap gap-1.5 text-sm">
              {x.pasos.map(p => (
                <li key={p.paso} className="rounded border border-linea px-1.5 py-0.5">
                  {p.hecho ? '✓' : '○'} {pasos[p.paso]}
                </li>
              ))}
            </ul>
          </div>
        )}
        {x?.comentarios && <p className="text-sm">{x.comentarios}</p>}
      </div>
    </div>
  );
}

type Valor = string | number | boolean | null;

// Valor del campo como se escribe en el formulario (las tasas en %).
function aTexto(tipo: string, v: Valor): string {
  if (v === null || v === undefined) return '';
  if (tipo === 'si_no') return v ? 'si' : 'no';
  if (tipo === 'tasa' && typeof v === 'number') return String(Math.round(v * 100 * 1e6) / 1e6);
  return String(v);
}

// Lo escrito vuelve al valor que guarda la app. Un numero invalido da undefined.
function aValor(tipo: string, texto: string): Valor | undefined {
  const t = texto.trim();
  if (!t) return null;
  if (tipo === 'si_no') return t === 'si';
  if (tipo === 'banco') return Number(t);
  if (tipo === 'usd' || tipo === 'tasa') {
    const n = Number(t.replace(/\s/g, '').replace(/,/g, '.'));
    if (!Number.isFinite(n)) return undefined;
    return tipo === 'tasa' ? n / 100 : n;
  }
  return t;
}

function FormularioDeFinanciacion({
  codigo,
  campos,
  version,
  onCerrar,
}: {
  codigo: string;
  campos: Record<string, Valor>;
  version: number | null;
  onCerrar: () => void;
}) {
  const guardar = useGuardarFinanciacion(codigo);
  const financiadores = useFinanciadores();
  // Bancos del financiador elegido (Bluequest da uno por exportacion).
  const bancos = useBancosDeFinanciador();
  const etiqueta = et(ETIQUETA_CAMPO_FINANCIACION, ETIQUETA_CAMPO_FINANCIACION_EN);
  const grupos = et(ETIQUETA_GRUPO_FINANCIACION, ETIQUETA_GRUPO_FINANCIACION_EN);
  const inicial = Object.fromEntries(
    CAMPOS_DE_FINANCIACION.map(c => [c.campo, aTexto(c.tipo, campos[c.campo] ?? null)]),
  );
  const [valores, setValores] = useState<Record<string, string>>(inicial);
  const [error, setError] = useState<string | null>(null);
  const enviar = async () => {
    setError(null);
    const cambios: Record<string, Valor> = {};
    for (const c of CAMPOS_DE_FINANCIACION) {
      if (valores[c.campo] === inicial[c.campo]) continue;
      const v = aValor(c.tipo, valores[c.campo] ?? '');
      if (v === undefined) {
        setError(tr(`"${valores[c.campo]}" no es un número.`, `"${valores[c.campo]}" is not a number.`));
        return;
      }
      cambios[c.campo] = v;
    }
    if (!Object.keys(cambios).length) return onCerrar();
    try {
      await guardar.mutateAsync({version, campos: cambios});
      onCerrar();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };
  const entrada = (c: (typeof CAMPOS_DE_FINANCIACION)[number]) => {
    const id = `fin-${c.campo}`;
    const cambiar = (v: string) => setValores(x => ({...x, [c.campo]: v}));
    const clase = 'mt-1 block w-full rounded-md border border-linea bg-white px-2 py-1 text-sm text-tinta';
    let control;
    if (c.tipo === 'si_no' || c.tipo === 'financiador')
      control = (
        <select id={id} value={valores[c.campo]} onChange={e => cambiar(e.target.value)} className={clase}>
          <option value="">—</option>
          {c.tipo === 'si_no' ? (
            <>
              <option value="si">{tr('Sí', 'Yes')}</option>
              <option value="no">{tr('No', 'No')}</option>
            </>
          ) : (
            financiadores.data
              // Los inactivos (LC y BC genericos) solo salen si ya estan elegidos.
              ?.filter(f => f.activo || f.codigo === valores[c.campo])
              .map(f => (
                <option key={f.codigo} value={f.codigo}>
                  {f.nombre === f.codigo ? f.codigo : `${f.nombre} (${f.codigo})`}
                </option>
              ))
          )}
        </select>
      );
    else if (c.tipo === 'instrumento') {
      const elegido = financiadores.data?.find(f => f.codigo === valores.financiador);
      const tipos = elegido?.instrumentos.length ? elegido.instrumentos : INSTRUMENTOS_DE_CREDITO;
      control = (
        <select id={id} value={valores[c.campo]} onChange={e => cambiar(e.target.value)} className={clase}>
          <option value="">—</option>
          {tipos.map(i => (
            <option key={i} value={i}>
              {et(ETIQUETA_INSTRUMENTO, ETIQUETA_INSTRUMENTO_EN)[i]}
            </option>
          ))}
        </select>
      );
    } else if (c.tipo === 'banco')
      control = (
        <select id={id} value={valores[c.campo]} onChange={e => cambiar(e.target.value)} className={clase}>
          <option value="">—</option>
          {(bancos.data?.bancos ?? [])
            .filter(b => b.financiador === valores.financiador && (b.activo || String(b.id) === valores[c.campo]))
            .map(b => (
              <option key={b.id} value={b.id}>
                {b.nombre}
              </option>
            ))}
        </select>
      );
    else if (c.campo === 'comentarios')
      control = (
        <textarea id={id} value={valores[c.campo]} rows={2} onChange={e => cambiar(e.target.value)} className={clase} />
      );
    else
      control = (
        <input
          id={id}
          type={c.tipo === 'fecha' ? 'date' : 'text'}
          inputMode={c.tipo === 'usd' || c.tipo === 'tasa' ? 'decimal' : undefined}
          value={valores[c.campo]}
          onChange={e => cambiar(e.target.value)}
          className={`${clase} ${c.tipo === 'usd' || c.tipo === 'tasa' ? 'text-right tabular-nums' : ''}`}
        />
      );
    return (
      <label
        key={c.campo}
        htmlFor={id}
        className={`block text-xs text-apagado ${c.campo === 'comentarios' ? 'sm:col-span-3' : ''}`}
      >
        {etiqueta[c.campo as CampoDeFinanciacion]}
        <NotaDeAyuda
          titulo={etiqueta[c.campo as CampoDeFinanciacion]}
          texto={F[c.campo as CampoDeFinanciacion]}
          dentroDeEtiqueta
        />
        {control}
      </label>
    );
  };
  return (
    <form
      className="space-y-4 rounded-lg border border-linea bg-papel p-4"
      aria-label={tr('Editar financiación', 'Edit financing')}
      onSubmit={e => {
        e.preventDefault();
        void enviar();
      }}
    >
      {GRUPOS_DE_FINANCIACION.map(g => (
        <fieldset key={g}>
          <legend className="text-xs font-semibold uppercase tracking-wide text-apagado">{grupos[g]}</legend>
          <div className="mt-1 grid gap-3 sm:grid-cols-3">
            {CAMPOS_DE_FINANCIACION.filter(c => c.grupo === g).map(entrada)}
          </div>
        </fieldset>
      ))}
      <p className="text-xs text-apagado">
        {tr(
          'Las tasas van en porcentaje (2 = 2 %). Lo que guardes aquí ya no lo cambia una nueva carga del Status BLQ.',
          'Rates go in percent (2 = 2 %). What you save here is no longer changed by a new Status BLQ load.',
        )}
      </p>
      {error && (
        <p role="alert" className="text-sm text-alerta">
          {error}
        </p>
      )}
      <div className="flex gap-2">
        <button
          type="submit"
          disabled={guardar.isPending}
          className="rounded-md bg-verde-oscuro px-3 py-1.5 text-sm text-white disabled:opacity-50"
        >
          {tr('Guardar', 'Save')}
        </button>
        <button
          type="button"
          onClick={onCerrar}
          className="rounded-md border border-linea bg-white px-3 py-1.5 text-sm hover:bg-papel"
        >
          {tr('Cancelar', 'Cancel')}
        </button>
      </div>
    </form>
  );
}
